import { config } from '../config';
import type { CapturePhase, MatchAnchors, MatchState } from '../types';
import { deriveMatchMinute } from './clock';
import { MATCH_STATE, TRANSITION_TABLE, isInMatch, requiresHold } from './machine';
import type { TransitionEvent, TransitionRow } from './machine';

/**
 * The **decisions** behind the §5.6 in-match chrome — pure, so a Node suite reaches all of them.
 *
 * Four things live here and nothing else:
 *
 * 1. §6.4a — **which** of SES-1's four confirming-hold transitions the shared top-zone slot renders
 *    for a given `Match.state`, and its label.
 * 2. SES-7 — whether the first-half overrun nudge is due, as a predicate over wall clock.
 * 3. LOCK-1 — whether the "Lock My 8" nudge is due, on the same terms (build unit U-9b).
 * 4. §6.4 — the notification strip's **priority order**, and the pick over it.
 *
 * None of these may read a clock or touch the DOM, which is why they are here rather than in the
 * components that render them: `no-restricted-syntax` refuses `Date` in `domain/`, so an implementer
 * cannot accidentally sample completion time where the caller was supposed to supply press-start.
 */

// ─────────────────────────────────────────────────────────────────────────────
// §6.4a — one control, four transitions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * SES-1's four clock-anchoring transitions. Every other `TransitionEvent` is a single tap.
 *
 * Declared as a union rather than derived from the table at the type level, and then **proved equal
 * to the table** by {@link HOLD_EVENTS} and its test. A type-level derivation would be unreadable;
 * a runtime equality against `TRANSITION_TABLE` is checkable in both directions, which is what
 * `doc-vs-code-drift-direction` actually asks for.
 */
export type HoldTransitionEvent = 'Kickoff' | 'Half' | 'Kickoff2H' | 'FullTime';

/**
 * §6.4a's label column, verbatim.
 *
 * `satisfies` makes the map total over the union, so adding a fifth confirming hold without giving
 * it a label is a compile error rather than a control that renders `undefined` at a venue.
 */
const HOLD_LABEL = {
  Kickoff: 'Hold to start',
  Half: 'Hold to end half',
  Kickoff2H: 'Hold to start 2nd half',
  FullTime: 'Hold to end match',
} as const satisfies Record<HoldTransitionEvent, string>;

export interface TransitionControl {
  readonly event: HoldTransitionEvent;
  /** §6.4a — *Hold to start* · *Hold to end half* · *Hold to start 2nd half* · *Hold to end match*. */
  readonly label: string;
  /** How long the press must be sustained. Carried so the fill animation and the timer agree. */
  readonly holdMs: number;
}

/** The events `TRANSITION_TABLE` marks as confirming holds, in table order. Exported for the two-way diff. */
export const HOLD_EVENTS: readonly TransitionEvent[] = [
  ...new Set(TRANSITION_TABLE.filter(requiresHold).map((row) => row.event)),
];

/**
 * §6.4a's state → control map, **derived from §2.2's table** rather than transcribed from §6.4a.
 *
 * Transcribing it would be a second transition table: §6.4a's own reason for existing is that
 * SES-1's four transitions were specified in one place, sited in another, given acceptance in a
 * third, and owned by nobody — a fifth copy is not the fix.
 *
 * **The cardinality gets a mechanism, not a comment.** §6.4a asserts *"from any given `Match.state`
 * exactly one of the four is legal"*, and this slot depends on that: it renders "the" control. A
 * claimed invariant with no enforcement is how a second hold row from one state would silently
 * become "whichever the filter happened to return first". So the builder throws — at module load,
 * which fails the build and every test, rather than at render time in front of a user.
 *
 * @throws {Error} if any state offers two confirming-hold transitions.
 */
export function buildHoldMap(table: readonly TransitionRow[]): ReadonlyMap<MatchState, TransitionControl> {
  const map = new Map<MatchState, TransitionControl>();
  for (const row of table) {
    if (!requiresHold(row)) continue;
    const event = row.event as HoldTransitionEvent;
    const existing = map.get(row.from);
    if (existing !== undefined) {
      throw new Error(
        `§6.4a: ${row.from} offers two confirming-hold transitions (${existing.event}, ${event}). ` +
          'The shared top-zone slot renders exactly one control, so the table must admit exactly one.',
      );
    }
    map.set(row.from, { event, label: HOLD_LABEL[event], holdMs: config.TRANSITION_HOLD_MS });
  }
  return map;
}

const HOLD_BY_STATE = buildHoldMap(TRANSITION_TABLE);

/**
 * The control the top-zone slot renders for this state, or `null` — *"any other state → **nothing**
 * — the slot keeps its height (§6.4)"*.
 *
 * The predicate is the **state, not the screen**, which is what makes one component satisfy §5.6's
 * *"reachable from Discovery and the Set Piece sheet, not only from the board"* as a property of one
 * element rather than a promise three screens each have to keep.
 */
export function transitionControlFor(state: MatchState): TransitionControl | null {
  return HOLD_BY_STATE.get(state) ?? null;
}

/**
 * Whether a capture surface is the right thing to render at all.
 *
 * `preMatch` counts: PRE-7's two exits hand the user to a capture surface **before** kickoff (§6.0),
 * and Kickoff is held from that surface's own top zone. Everything from `fullTime` onward is a
 * lifecycle step with its own screens (U-14, U-16), not a capture surface.
 */
export function isCaptureSurfaceState(state: MatchState): boolean {
  return state === MATCH_STATE.preMatch || transitionControlFor(state) !== null;
}

// ─────────────────────────────────────────────────────────────────────────────
// SES-7 — the first-half overrun nudge
// ─────────────────────────────────────────────────────────────────────────────

/**
 * SES-7 — *"If H1 elapsed exceeds `halfLengthMin + HALF_OVERRUN_NUDGE_MIN`, show a non-blocking
 * 'end the half?' nudge. It MUST NOT auto-transition."*
 *
 * A predicate over wall clock, evaluated on every render — **never a `setTimeout`**. §2.4 is explicit
 * that *"nudge deadlines are absolute wall-clock values recomputed on every render and on resume,
 * never `setTimeout` state"*, and the reason is iOS: a timer armed before the phone went into a
 * pocket may never fire, so a nudge that depended on one would simply not appear at the break.
 *
 * Compared on the raw anchor rather than on the derived whole minute, because "exceeds" is a claim
 * about elapsed time and `deriveMatchMinute` floors. At `halfLengthMin + HALF_OVERRUN_NUDGE_MIN`
 * exactly, elapsed does not yet exceed the threshold and the nudge is not due.
 */
export function overrunNudgeDue(anchors: MatchAnchors, state: MatchState, now: number): boolean {
  if (state !== MATCH_STATE.firstHalf) return false;
  const kickoff = anchors.kickoffTs;
  if (kickoff === null) return false;
  const thresholdMs = (anchors.halfLengthMin + config.HALF_OVERRUN_NUDGE_MIN) * 60_000;
  return now - kickoff > thresholdMs;
}

// ─────────────────────────────────────────────────────────────────────────────
// LOCK-1 — the lock nudge
// ─────────────────────────────────────────────────────────────────────────────

/** What {@link lockNudgeDue} reads: the clock anchors, plus the phase LOCK-1 is scoped to. */
export interface LockNudgeInput extends MatchAnchors {
  readonly capturePhase: CapturePhase;
}

/**
 * LOCK-1 — *"From a configurable minute, default `LOCK_NUDGE_MIN`, a non-blocking 'Lock My 8'
 * affordance appears. **The app never auto-locks.**"* The *configurable* half of that sentence is
 * {@link lockNudgeDue}'s `thresholdMin`, added by build unit B-19a under a stakeholder ruling —
 * see the note at the bottom of this comment.
 *
 * Three conditions, and each is a separate clause of the requirement rather than a defensive check:
 *
 * - **`capturePhase === 'discovery'`.** There is nothing to nudge a locked board towards; §6.4 says
 *   the affordance *"disappears on lock, at which point SES-7 takes the slot"*. This is the whole of
 *   that sentence — the strip re-picks from live predicates every render (`pickStripNotice`), so
 *   "disappears" needs no separate teardown.
 * - **an in-match state.** Before kickoff the derived minute is `0` and the nudge would be a
 *   permanent fixture on the Pre-Match hand-off surface; after Full Time the board is history.
 * - **`deriveMatchMinute ≥ thresholdMin`.** Read through the single temporal authority
 *   (INV-7/INV-10), never off a second elapsed-time calculation, and evaluated **as a predicate over
 *   wall clock on every render** — §2.4: *"nudge deadlines are absolute wall-clock values recomputed
 *   on every render and on resume, never `setTimeout` state"*, because iOS may never fire a timer
 *   armed before the phone went into a pocket.
 *
 * `≥` rather than `>`: LOCK-1 says *"from"* the minute, so the affordance is present at
 * `thresholdMin` itself. (SES-7's `overrunNudgeDue` uses `>` because its requirement says
 * *"exceeds"*. The asymmetry is the two requirements' wording, not an oversight.)
 *
 * **The nudge is an offer, and nothing here locks anything.** `ConfirmLock` is dispatched only by
 * LOCK-4's confirm, which is a user tap on the LOCK-3 review screen.
 *
 * ── `thresholdMin` — build unit B-19a, a **ruled** extension ──────────────────
 *
 * LOCK-1 has said *"from a **configurable** minute, default `LOCK_NUDGE_MIN`"* since revision 1,
 * and BAK-5 has carried a `lockNudgeMin` setting since U-5 — but this predicate took no threshold,
 * so nothing anywhere could read that setting. B-19 found the gap while building B's More tab
 * (spec §2.4 lists *"lock nudge minutes"* in it) and **stopped rather than working around it**:
 * adding a threshold changes a working engine predicate, which requirements §5 clause 5 says is
 * flagged and never silently changed, and restating the predicate in B's own layer is clause 6's
 * *"cloning is altering"*. Andrew ruled on the flag: *"I'm fine with the engine extension for this
 * specific purpose."* This parameter is the whole of what that authorized.
 *
 * **The default is the constant by reference, not a literal that equals it.** That is what makes
 * every pre-existing caller — A's `ui/capture/NotificationStrip.tsx`, and every test written
 * against it — behave exactly as before, byte for byte, rather than as before *for now*.
 *
 * **What this parameter deliberately does not do is validate itself.** A minute count arriving
 * from storage is `unknown` (the settings row's own type) and may have come from an archive
 * written by another device, so it has to be parsed somewhere; parsing it *here* would put a
 * second opinion about the sane band beside the one in the reader, and the two would eventually
 * disagree. B's `ui-b/lockNudge.ts` owns that parse — deny-by-default back to `LOCK_NUDGE_MIN` —
 * and it is the only place a value crosses into this parameter.
 */
export function lockNudgeDue(
  match: LockNudgeInput,
  state: MatchState,
  now: number,
  thresholdMin: number = config.LOCK_NUDGE_MIN,
): boolean {
  if (match.capturePhase === 'locked') return false;
  if (!isInMatch(state)) return false;
  return deriveMatchMinute(match, state, now) >= thresholdMin;
}

// ─────────────────────────────────────────────────────────────────────────────
// §6.4 — the notification strip's one slot
// ─────────────────────────────────────────────────────────────────────────────

/**
 * The strip's priority order, highest first.
 *
 * §6.4 fixes four of these and argues the order: *"`LOCK-1 > SES-7 > SYN-3 error > OBS-4`. LOCK-1 is
 * a MUST and SES-7 a SHOULD; the reverse order would mean a user still in Discovery past
 * `halfLengthMin + HALF_OVERRUN_NUDGE_MIN` never sees 'Lock My 8' at all — a MUST suppressed by a
 * SHOULD."*
 *
 * §5.6 routes two more conditions into the same slot — the **§13.7 writer banner** and the
 * **wake-lock failure chip** — without stating where they sit. They are placed below `SYN-3` and
 * above `OBS-4`, and the reasoning is §6.4's own: `lockNudge` and `overrun` are time-boxed prompts
 * the user must act on *now*, and `syncError` is a claim about whether the match is protected;
 * `writerBanner` and `wakeLock` are **standing conditions** that re-appear the instant a higher slot
 * clears, so deferring them costs nothing a user can lose. Putting either at the top would be the
 * failure §6.4 names — a persistent banner would suppress LOCK-1 for the whole match.
 *
 * **The four §6.4 entries remain a subsequence of this list, and a test asserts it in both
 * directions**, so a later unit adding a sixth condition cannot quietly reinterpret the order the
 * design actually settled.
 */
export const STRIP_PRIORITY = [
  /** LOCK-1 — "Lock My 8". Build unit U-9b. */
  'lockNudge',
  /** SES-7 — the first-half overrun nudge. */
  'overrun',
  /** SYN-3 / NFR-8 — a non-blocking sync error. Build unit U-18. */
  'syncError',
  /** ENV-10 / §13.7 — another live context holds the writer lock. */
  'writerBanner',
  /** ENV-8 / NFR-8 — the screen wake lock was refused or is unavailable. */
  'wakeLock',
  /** OBS-4 — the service-worker update toast. Build unit U-20. */
  'update',
] as const;

export type StripNoticeKind = (typeof STRIP_PRIORITY)[number];

/** The four entries §6.4 orders by name. The others are §5.6's, placed rather than specified. */
export const STRIP_PRIORITY_FROM_SPEC: readonly StripNoticeKind[] = [
  'lockNudge',
  'overrun',
  'syncError',
  'update',
];

/**
 * The one notice the strip shows, or `null`.
 *
 * *"A suppressed lower-priority nudge is queued, not dropped."* That is discharged by this being a
 * **pick over live predicates** rather than a consuming queue: every candidate is recomputed each
 * render from the state that makes it true, so a suppressed notice re-appears by itself the moment
 * the higher one stops being true. A real queue would need something to remember, and something to
 * remember is something that can be lost.
 */
export function pickStripNotice(active: readonly StripNoticeKind[]): StripNoticeKind | null {
  const set = new Set(active);
  return STRIP_PRIORITY.find((kind) => set.has(kind)) ?? null;
}
